import apiService, { ApiError } from './apiService';

/**
 * Shopper Payout Service
 * Handles the bulk import (CSV) flow for shopper payouts.
 *
 * Backend lifecycle of an import batch:
 *   pending -> validating -> validated -> processing
 *           -> completed | completed_with_errors | failed_incomplete
 *   (any in-flight state can also end up as `canceled`)
 */

const BASE_PATH = '/api/v1/admin/shopper-payout';

/** Import batch status values sent by the backend. */
export const IMPORT_STATUS = {
  PENDING: 'pending',
  VALIDATING: 'validating',
  VALIDATED: 'validated',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  COMPLETED_WITH_ERRORS: 'completed_with_errors',
  FAILED_INCOMPLETE: 'failed_incomplete',
  CANCELED: 'canceled',
};

/** Statuses where the backend is still working and the FE must keep polling. */
export const IN_PROGRESS_STATUSES = [
  IMPORT_STATUS.VALIDATING,
  IMPORT_STATUS.PROCESSING,
];

/** Statuses where a batch is still "open" — the admin is mid-flow on it. */
export const OPEN_STATUSES = [
  IMPORT_STATUS.PENDING,
  IMPORT_STATUS.VALIDATING,
  IMPORT_STATUS.VALIDATED,
  IMPORT_STATUS.PROCESSING,
];

/**
 * Statuses a batch can still be canceled from. `processing` is deliberately
 * excluded: once confirmed, payouts are being executed and already-paid rows
 * cannot be taken back, so leaving the page must never try to stop it.
 */
export const CANCELABLE_STATUSES = [
  IMPORT_STATUS.PENDING,
  IMPORT_STATUS.VALIDATING,
  IMPORT_STATUS.VALIDATED,
];

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const MAX_ROWS = 10000;

/**
 * Copy taken from the design for the client-side size guard.
 * The backend rejects oversized uploads with a plain-text HTTP 413 that is not
 * safe to render, so the FE checks the size before uploading.
 */
export const FILE_TOO_LARGE_MESSAGE =
  'The file exceeds the maximum allowed size of 10 MB.\nPlease split the data into multiple CSV files and try again.';

class ShopperPayoutService {
  /**
   * Validate a file locally before sending it to the backend.
   * @param {File} file
   * @returns {string|null} Error message ready to display, or null when the file passes.
   */
  getFileRejectionMessage(file) {
    if (!file) return 'Please select a CSV file.';
    if (file.size === 0) return 'The file is empty. Please select a CSV file.';
    if (file.size > MAX_FILE_SIZE_BYTES) return FILE_TOO_LARGE_MESSAGE;
    return null;
  }

  /**
   * Normalize an error coming from the API into a message ready to display.
   * @param {Error} error
   * @param {string} fallback
   * @returns {string}
   */
  getErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
    if (error instanceof ApiError) {
      // The upload endpoint answers with a plain-text 413 when the multipart
      // body is over the server limit — swap in the copy from the design.
      if (error.status === 413) return FILE_TOO_LARGE_MESSAGE;
      if (error.message) return error.message;
    }
    return error?.message || fallback;
  }

  /**
   * Format a "database style" decimal string (e.g. "10.75000000") for display.
   * @param {string|number} value
   * @param {Object} options
   * @param {boolean} options.grouped - Add thousands separators.
   * @returns {string} e.g. "10.75" / "1,234.50"
   */
  formatAmount(value, { grouped = true } = {}) {
    if (value === null || value === undefined || value === '') return '0.00';
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return String(value);
    return parsed.toLocaleString(grouped ? 'en-US' : undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: grouped,
    });
  }

  /**
   * Format a record count for display.
   * @param {number} value
   * @returns {string} e.g. "1,250"
   */
  formatCount(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '0';
    return parsed.toLocaleString('en-US');
  }

  /**
   * Format a file size in bytes for display.
   * @param {number} bytes
   * @returns {string} e.g. "1.8 MB"
   */
  formatFileSize(bytes) {
    const size = Number(bytes);
    if (!Number.isFinite(size) || size <= 0) return '0 KB';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Progress ratio (0..1) for the current stage of a batch.
   * Validation counts checked rows; processing counts rows already paid out.
   * @param {Object} batch
   * @returns {number}
   */
  getProgressRatio(batch) {
    if (!batch) return 0;

    if (batch.status === IMPORT_STATUS.PROCESSING) {
      const done = (batch.successRows || 0) + (batch.failedRows || 0);
      const total = batch.validRows || 0;
      if (!total) return 0;
      return Math.min(done / total, 1);
    }

    const total = batch.totalRows || 0;
    if (!total) return 0;
    return Math.min((batch.checkedRows || 0) / total, 1);
  }

  /**
   * Download the blank CSV template.
   * @param {Object} options
   * @returns {Promise<{content: string, fileName: string}>}
   */
  async getTemplate(options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        `${BASE_PATH}/template`,
        {
          method: 'GET',
          responseType: 'text',
          ...options,
        }
      );

      return {
        content: response?.data ?? '',
        fileName: parseFileNameFromDisposition(
          response?.headers?.['content-disposition'],
          'shopper_payout_template.csv'
        ),
      };
    } catch (error) {
      throw toServiceError(error, 'Failed to download the CSV template');
    }
  }

  /**
   * Upload a CSV file and create an import batch.
   * File-level rejections (encoding, size, row count, ragged columns, empty
   * file) come back as HTTP 400 with a message ready to display.
   * @param {File} file
   * @param {Object} options
   * @returns {Promise<Object>} The created batch.
   */
  async uploadFile(file, options = {}) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiService.skaiOneRequest(`${BASE_PATH}/import`, {
        method: 'POST',
        data: formData,
        timeout: 120000,
        ...options,
      });

      return unwrap(response, 'import upload');
    } catch (error) {
      throw toServiceError(error, 'Failed to upload the CSV file');
    }
  }

  /**
   * Kick off asynchronous validation of an uploaded batch.
   * @param {string} id
   * @param {Object} options
   * @returns {Promise<Object>} The batch, now in `validating`.
   */
  async startValidation(id, options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        `${BASE_PATH}/import/${id}/validate`,
        { method: 'POST', ...options }
      );
      return unwrap(response, 'import validation');
    } catch (error) {
      throw toServiceError(error, 'Failed to start validation');
    }
  }

  /**
   * Read the current state of a batch. This is the polling endpoint used while
   * a batch is validating or processing.
   * @param {string} id
   * @param {Object} options
   * @returns {Promise<Object>}
   */
  async getImport(id, options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        `${BASE_PATH}/import/${id}`,
        { method: 'GET', ...options }
      );
      return unwrap(response, 'import detail');
    } catch (error) {
      throw toServiceError(error, 'Failed to load the import');
    }
  }

  /**
   * Read a page of rows of a batch.
   * @param {string} id
   * @param {Object} params
   * @param {'all'|'valid'|'invalid'} params.result - Which rows to return.
   * @param {number} params.page
   * @param {number} params.size
   * @param {Object} options
   * @returns {Promise<Object>} Paginated envelope: {page,pageCount,size,count,total,data}
   */
  async getRows(id, params = {}, options = {}) {
    const { result = 'all', page = 1, size = 20 } = params;

    try {
      const response = await apiService.skaiOneRequest(
        `${BASE_PATH}/import/${id}/rows`,
        {
          method: 'POST',
          data: { result, page, size },
          ...options,
        }
      );
      return unwrap(response, 'import rows');
    } catch (error) {
      throw toServiceError(error, 'Failed to load the payout rows');
    }
  }

  /**
   * Confirm a validated batch — this starts the actual payout processing.
   * Only rows marked `valid` are processed.
   * @param {string} id
   * @param {Object} options
   * @returns {Promise<Object>} The batch, now in `processing`.
   */
  async confirmImport(id, options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        `${BASE_PATH}/import/${id}/confirm`,
        { method: 'POST', ...options }
      );
      return unwrap(response, 'import confirm');
    } catch (error) {
      throw toServiceError(error, 'Failed to confirm the import');
    }
  }

  /**
   * Cancel a batch that has not been processed yet. Used when the admin leaves
   * the import page or replaces the selected file.
   * @param {string} id
   * @param {Object} options
   * @returns {Promise<void>}
   */
  async cancelImport(id, options = {}) {
    try {
      await apiService.skaiOneRequest(`${BASE_PATH}/import/${id}/cancel`, {
        method: 'POST',
        ...options,
      });
    } catch (error) {
      throw toServiceError(error, 'Failed to cancel the import');
    }
  }

  /**
   * Cancel a batch without surfacing failures — used on unmount, where there is
   * no UI left to report an error to.
   * @param {string} id
   */
  cancelImportSilently(id) {
    if (!id) return;
    this.cancelImport(id).catch(() => {
      /* best effort — the batch stays open and can be canceled server-side */
    });
  }

  /**
   * Download the rows of a batch as CSV, using the same schema as the template
   * so the file can be corrected and re-uploaded.
   *
   * Note: the backend does not send `Access-Control-Expose-Headers`, so the
   * browser cannot read `content-disposition` cross-origin — pass
   * `fallbackFileName` to keep the saved file recognizable.
   *
   * @param {string} id
   * @param {'all'|'invalid'} scope
   * @param {Object} config
   * @param {string} config.fallbackFileName
   * @returns {Promise<{content: string, fileName: string}>}
   */
  async downloadRows(id, scope = 'all', config = {}) {
    const { fallbackFileName, ...options } = config;

    try {
      const response = await apiService.skaiOneRequest(
        `${BASE_PATH}/import/${id}/download`,
        {
          method: 'GET',
          params: { scope },
          responseType: 'text',
          ...options,
        }
      );

      return {
        content: response?.data ?? '',
        fileName: parseFileNameFromDisposition(
          response?.headers?.['content-disposition'],
          fallbackFileName || `shopper_payout_${scope}.csv`
        ),
      };
    } catch (error) {
      throw toServiceError(error, 'Failed to download the CSV file');
    }
  }

  /**
   * List previously imported batches (History Import).
   * @param {Object} params
   * @param {string} params.keyword - File name or batch code.
   * @param {string} params.dateFrom - YYYY-MM-DD
   * @param {string} params.dateTo - YYYY-MM-DD
   * @param {number} params.page
   * @param {number} params.size
   * @param {Object} options
   * @returns {Promise<Object>} Paginated envelope.
   */
  async getImportList(params = {}, options = {}) {
    const { keyword, dateFrom, dateTo, page = 1, size = 20 } = params;

    const requestData = { page, size };

    if (keyword && keyword.trim()) {
      requestData.keyword = keyword.trim();
    }

    if (dateFrom && dateTo) {
      requestData.dateFilter = {
        start: formatDateOnly(dateFrom),
        end: formatDateOnly(dateTo),
      };
    }

    try {
      const response = await apiService.skaiOneRequest(`${BASE_PATH}/list`, {
        method: 'POST',
        data: requestData,
        ...options,
      });
      return unwrap(response, 'import list');
    } catch (error) {
      throw toServiceError(error, 'Failed to load the import history');
    }
  }
}

/**
 * Save a text payload as a file download in the browser.
 * @param {string} content
 * @param {string} fileName
 */
export const saveTextAsFile = (content, fileName) => {
  // `response.text()` strips the UTF-8 BOM the backend writes; put it back so
  // Excel reads accented shopper names correctly.
  const withBom = content.startsWith('\ufeff') ? content : `\ufeff${content}`;
  const blob = new Blob([withBom], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Pull the file name out of a content-disposition header.
 * @param {string} disposition
 * @param {string} fallback
 * @returns {string}
 */
const parseFileNameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (!match || !match[1]) return fallback;
  try {
    return decodeURIComponent(match[1].trim());
  } catch (error) {
    return match[1].trim();
  }
};

/**
 * Normalize a date coming from the date range picker to YYYY-MM-DD.
 * @param {string|Date} date
 * @returns {string|null}
 */
const formatDateOnly = date => {
  if (!date) return null;
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Pull `data` out of the API envelope, failing loudly when it is missing.
 * @param {Object} response
 * @param {string} label
 */
const unwrap = (response, label) => {
  if (!response || response.data === undefined || response.data === null) {
    throw new ApiError(
      `Invalid response format from the ${label} API`,
      200,
      'INVALID_RESPONSE'
    );
  }
  return response.data;
};

/**
 * Keep ApiError instances (they carry the backend message) and wrap anything
 * else so callers always get a predictable shape.
 * @param {Error} error
 * @param {string} message
 */
const toServiceError = (error, message) => {
  if (error instanceof ApiError) return error;
  return new ApiError(message, 0, 'SHOPPER_PAYOUT_ERROR', {
    originalError: error?.message,
  });
};

const shopperPayoutService = new ShopperPayoutService();

export default shopperPayoutService;
export { ShopperPayoutService };
